/**
 * The web's calls to the sign-in service (#360; ADR 0008), mounted by nginx at `/api/identity`.
 *
 * All of them are same-origin `fetch`es, so the browser attaches the httpOnly session cookie and
 * sends `Origin: https://groundwork-sports.com` by itself on a POST, which is what the service
 * and FastAPI both check. Nothing here reads or stores a token: the service answers a sign-in with
 * a redirect and a `Set-Cookie` the page never sees.
 *
 * Kept apart from `auth.ts` because those functions are storage only and `client.ts` imports them;
 * this file needs `BASE_URL` from `client.ts`.
 */

import { signOutAnalytics } from '../analytics'
import type { SignedInDevice } from '../lib/devices'
import { authHeaders, clearToken, getToken, isDevSession, type Provider } from './auth'
import { BASE_URL } from './client'

const IDENTITY = `${BASE_URL}/api/identity`

/** Where the browser lands once the service has set the cookie. A page of ours, `SignedIn.tsx`. */
export const SIGNED_IN_PATH = '/signed-in'

/** Where the service sends a refusal, with `?error=<CODE>`. The sign-in page. */
export const SIGN_IN_ERROR_PATH = '/login'

export class SignInError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, code: string | null) {
    super(`sign-in service answered ${status}${code ? ` ${code}` : ''}`)
    this.name = 'SignInError'
    this.status = status
    this.code = code
  }
}

/**
 * Starts a sign-in with *provider*: asks the service for the provider's authorize URL and goes
 * there. Apple answers by POSTing the person back to the service's callback, which sets the
 * cookie and redirects to `SIGNED_IN_PATH` (or to `SIGN_IN_ERROR_PATH?error=<CODE>`). Throws
 * `SignInError` when the service refuses to start one (no such provider, rate limited).
 *
 * *inviteCode* is the code of the invite the person arrived with (the invite page, `Invite.tsx`).
 * It rides in `additionalData.inviteCode`: the release keeps `additionalData` in the OAuth state
 * it stores for the round trip to the provider (server-side, bound to this browser by the state
 * cookie), and the service reads it back in the callback, so the code is in no URL and no cookie of
 * ours. An invite admits a new person even while regular sign-up is closed (S-O32); it is never
 * required, and an existing user who sends one just signs in.
 */
export async function beginSignIn(provider: Provider, { inviteCode }: { inviteCode?: string } = {}): Promise<void> {
  const res = await fetch(`${IDENTITY}/sign-in/social`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      provider,
      callbackURL: SIGNED_IN_PATH,
      errorCallbackURL: SIGN_IN_ERROR_PATH,
      ...(inviteCode ? { additionalData: { inviteCode } } : {}),
    }),
  })
  const body = (await res.json().catch(() => null)) as { url?: string; code?: string } | null
  if (!res.ok || !body?.url) throw new SignInError(res.status, body?.code ?? null)
  window.location.assign(body.url)
}

/**
 * One call to the sign-in service with this browser's credentials: the cookie by itself, or a
 * Developer Mode bearer. Unlike `apiFetch` a 401 does not send the person to the sign-in page:
 * a Developer Mode button signs in with a legacy token, which the service does not know, and the
 * tab that asked should say it could not load, not leave the page. Throws `SignInError`.
 */
async function identity<T>(path: string, init: { method: 'GET' | 'POST'; body?: unknown }): Promise<T> {
  const res = await fetch(`${IDENTITY}${path}`, {
    method: init.method,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: init.method === 'POST' ? JSON.stringify(init.body ?? {}) : undefined,
  })
  const body = (await res.json().catch(() => null)) as T | { code?: string } | null
  if (!res.ok) throw new SignInError(res.status, (body as { code?: string } | null)?.code ?? null)
  return body as T
}

/**
 * Where this person is signed in (#367; SI-D17): every live sign-in session of theirs, this
 * browser's first. The service answers a kind of device and a client as ids, a last-used instant,
 * and nothing else (no token, no address, no user agent).
 */
export async function listSignInDevices(): Promise<SignedInDevice[]> {
  const answer = await identity<{ devices?: SignedInDevice[] }>('/devices', { method: 'GET' })
  if (!Array.isArray(answer.devices)) return []
  // A row with no id or no readable date cannot be shown or acted on; the person still sees the others
  // (iOS drops such a row the same way: `SignedInDevicesWire.decode`).
  return answer.devices.filter(
    (device) => typeof device?.id === 'string' && device.id !== '' && !Number.isNaN(Date.parse(device.lastActiveAt)),
  )
}

/** Signs another device out (SI-D17). The service answers success for a device that is already gone. */
export async function revokeSignInDevice(id: string): Promise<void> {
  await identity<{ status: boolean }>('/devices/revoke', { method: 'POST', body: { id } })
}

/** "Sign out everywhere else" (SI-D20): every other device of this person, in one step. */
export async function revokeOtherSignInDevices(): Promise<void> {
  await identity<{ status: boolean }>('/devices/revoke-others', { method: 'POST' })
}

/** The providers this account can sign in with (the release's own `list-accounts`). Read only: the web changes nothing (SI-D21). */
export async function listSignInProviders(): Promise<Provider[]> {
  const answer = await identity<Array<{ providerId?: unknown }>>('/list-accounts', { method: 'GET' })
  const held = Array.isArray(answer) ? answer.map((account) => account.providerId) : []
  return (['apple', 'google'] as const).filter((provider) => held.includes(provider))
}

/**
 * The address a paused sign-up (a refusal whose verified email already belongs to an account,
 * `?error=ACCOUNT_EXISTS`) is about, for the dialog over the sign-in page (#364; SI-D8, SI-D9).
 *
 * The service holds it, named by an httpOnly cookie it set with the refusal that this page's script
 * cannot see, and answers it ONCE: the first read spends it. It is the address of the identity the
 * person just used, never the existing account's. Null when the service has none to give (a second
 * read, an expired cookie, an identity with no verified address) or cannot be reached; the dialog
 * then opens without the address line, because its sentence needs none.
 */
export async function readPausedSignUp(): Promise<string | null> {
  try {
    const res = await fetch(`${IDENTITY}/paused-sign-up`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    const body = (await res.json()) as { email?: unknown } | null
    const email = typeof body?.email === 'string' ? body.email.trim() : ''
    return email !== '' && email.length <= 254 ? email : null
  } catch {
    return null
  }
}

/**
 * A browser that signed in before the web moved to cookies still holds that sign-in's bearer in
 * `localStorage`, with no developer marker (only a Developer Mode link or button writes the marker).
 * That token never expires (R6) and would shadow the cookie (a bearer decides alone), so the first
 * load after the change drops it, asks the server to revoke it (best effort: the person is on their
 * way to sign in again either way), and the app sends them to the sign-in page once: "sign in once
 * more on the new web sign-in, and then have it last" (spec, story 34). A Developer Mode token keeps working.
 */
export function dropLegacyWebToken(): void {
  const token = getToken()
  if (token === null || isDevSession()) return
  clearToken()
  const legacy = !token.includes('.')
  void fetch(legacy ? `${BASE_URL}/api/auth/logout` : `${IDENTITY}/sign-out`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: legacy ? undefined : '{}',
  }).catch(() => {})
}

/**
 * Ends this browser's sign-in session and forgets it here. The session is a row the service owns,
 * so the service deletes it (and clears the cookie, which only a response can do): FastAPI refuses
 * the cookie from the next request. A Developer Mode session made by the legacy "Continue as
 * developer" button is a legacy token (no dot in it), which FastAPI revokes; a signed one from
 * `dev_login_link.py` is a service session and goes through the service like a cookie does.
 *
 * Throws, leaving everything as it was, when the session could not be ended: signing out must
 * not claim a success it did not have. A 401 means there was no session, which is what the
 * person wanted.
 *
 * Once the session has ended the analytics module is told (#708): the person's reference and Google's cookies go, so
 * the next person on this browser is a new visitor to it. It answers whether the page must END WITH A FULL PAGE LOAD
 * (`window.location.assign('/login')`) instead of a navigation: true when the container is on the page, because a
 * container cannot leave a page it is on, and nothing of the signed-in page life may survive into the next sign-in.
 */
export async function signOut(): Promise<{ fullPageLoad: boolean }> {
  const token = getToken()
  const legacy = token !== null && !token.includes('.')
  const res = await fetch(legacy ? `${BASE_URL}/api/auth/logout` : `${IDENTITY}/sign-out`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: legacy ? undefined : '{}',
  })
  if (!res.ok && res.status !== 401) throw new SignInError(res.status, null)
  clearToken()
  return signOutAnalytics()
}
